import { QueuedJob, QueuedJobSkippedReason, QueuedJobType } from './queued-job.type'

export class QueuedJobModel {
  static readonly PANEL_TITLE = 'Queued jobs'

  static readonly TITLE_BY_TYPE: Readonly<Record<QueuedJobType, string>> = {
    sending_invitations: 'Sending invitations',
    importing_entries: 'Importing entries',
    adding_entries: 'Adding entries'
  }

  static readonly RESULT_LABEL_BY_TYPE: Readonly<Record<QueuedJobType, string>> = {
    sending_invitations: 'Sent',
    importing_entries: 'Imported',
    adding_entries: 'Added'
  }

  static readonly SKIPPED_LABEL = 'Skipped'

  static readonly SKIPPED_DETAILS_LABEL = 'Skipped details'

  static readonly EMPTY_STATE_MESSAGE = 'No queued jobs yet.'

  static readonly LOAD_ERROR_FALLBACK_MESSAGE = 'We couldn’t load your queued jobs.'

  static readonly LOADING_LABEL = 'Loading queued jobs'

  static readonly SEE_PREVIOUS_JOBS_LABEL = 'See previous jobs'

  static readonly DOWNLOAD_REPORT_LABEL = 'Download report'

  static readonly DOWNLOAD_REPORT_FALLBACK_FILENAME = 'import-report.csv'

  static readonly DOWNLOAD_REPORT_ERROR_FALLBACK_MESSAGE = 'We couldn’t download the report.'

  static readonly SOCKET_RECONNECT_DELAYS_MS: readonly number[] = [1000, 2000, 5000, 10000]

  static readonly PANEL_REFRESH_INTERVAL_MS = 5000

  static readonly SKELETON_THEME: Readonly<Record<'title' | 'meta' | 'eventChip' | 'personaChip', Record<string, string>>> = {
    title: { width: '192px', height: '16px', 'border-radius': '4px', 'margin-bottom': '0' },
    meta: { width: '64px', height: '12px', 'border-radius': '4px', 'margin-bottom': '0' },
    eventChip: { width: '96px', height: '22px', 'border-radius': '6px', 'margin-bottom': '0' },
    personaChip: { width: '48px', height: '22px', 'border-radius': '6px', 'margin-bottom': '0' }
  }

  static readonly FAILED_FALLBACK_MESSAGE = 'Job failed. We’ve reported the issue to support.'

  /**
   * Backoff before the next socket reconnect attempt: 1s → 2s → 5s, then 10s for every later attempt.
   * @param attempt {number} - 1-based reconnect attempt since the last successful message.
   * @returns Delay in milliseconds.
   */
  static socketReconnectDelayMs(attempt: number): number {
    const delays = QueuedJobModel.SOCKET_RECONNECT_DELAYS_MS
    return delays[Math.min(Math.max(attempt, 1), delays.length) - 1]
  }

  /**
   * Download report is offered only for completed imports that have a report file.
   * @param job {QueuedJob} - Queued job from the API.
   * @returns Whether the Download report button is shown.
   */
  static canDownloadReport(job: QueuedJob): boolean {
    return job.type === 'importing_entries' && job.status === 'completed' && !!job.file_url
  }

  /**
   * File name for the downloaded report, taken from the last path segment of `file_url`.
   * @param fileUrl {string} - Report URL from the API.
   * @returns Report file name, or the fallback when it can't be read.
   */
  static reportFilename(fileUrl: string): string {
    const lastSegment = fileUrl.split(/[?#]/)[0].split('/').pop()
    if (!lastSegment) return QueuedJobModel.DOWNLOAD_REPORT_FALLBACK_FILENAME
    try {
      return decodeURIComponent(lastSegment)
    } catch {
      return lastSegment
    }
  }

  /**
   * Rows handled so far (succeeded + skipped), capped at the job total.
   * @param job {QueuedJob} - Queued job from the API.
   * @returns Processed rows count.
   */
  static processedCount(job: QueuedJob): number {
    return Math.min(job.succeeded + job.total_skipped, job.total)
  }

  /**
   * Progress of the job as a percentage (0–100).
   * @param job {QueuedJob} - Queued job from the API.
   * @returns Progress percentage.
   */
  static progressPercent(job: QueuedJob): number {
    if (!job.total) return 0
    return (QueuedJobModel.processedCount(job) / job.total) * 100
  }

  /**
   * Skipped reasons with a count above zero, labelled from the backend keys
   * (e.g. `already_in_target_persona_collection_count` → "Already in target persona collection").
   * @param job {QueuedJob} - Queued job from the API.
   * @returns Skipped reasons to list in the tooltip.
   */
  static skippedReasons(job: QueuedJob): QueuedJobSkippedReason[] {
    return Object.entries(job.skipped)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({ label: QueuedJobModel._skippedReasonLabel(key), count }))
  }

  /**
   * Skipped reason label from the backend key
   * @param key {string} - Backend key.
   * @returns Skipped reason label.
   */
  private static _skippedReasonLabel(key: string): string {
    const words = key
      .replace(/_count$/, '')
      .replace(/_/g, ' ')
      .trim()
    return words.charAt(0).toUpperCase() + words.slice(1)
  }
}
